<script setup lang="ts">
import type { ParsedPhoneNumber } from 'awesome-phonenumber'
import type { FormKitFrameworkContext } from '@formkit/core'
import { has } from '@formkit/utils'
import { createMessage } from '@formkit/core'

const props = defineProps<{
  context: FormKitFrameworkContext
}>()

const context = props.context
const node = context.node

if (!context._value) {
  context._value = {}
  context._value.regionCode = 'DE'
  context._value.phonenumber = ''
}

const { $parsePhoneNumber } = useNuxtApp()

function handleSelect(e: Event) {
  const target = e.currentTarget as HTMLInputElement
  context._value.regionCode = target.value

  node.input({
    regionCode: context._value.regionCode,
    phonenumber: context._value.phonenumber,
    parsedPhoneNumber: {},
  })
}

function handleInput(e: Event) {
  const target = e.currentTarget as HTMLInputElement
  context._value.phonenumber = target.value

  const pn: ParsedPhoneNumber = $parsePhoneNumber(context._value.phonenumber, {
    regionCode: context._value.regionCode,
  })

  if (!pn.valid || !pn.typeIsMobile) {
    const message = createMessage({
      key: 'rule_tel',
      value: node.props.validationMessages?.rule_tel as string,
      blocking: true,
      visible: false,
    })

    node.store.set(message)
  } else {
    if (has(node.store, 'rule_tel')) {
      node.store.remove('rule_tel')
    }
  }

  node.input({
    regionCode: context._value.regionCode,
    phonenumber: context._value.phonenumber,
    parsedPhoneNumber: {},
  })
}

function handleBlur(e: FocusEvent) {
  const message = createMessage({
    key: 'rule_tel',
    value: node.props.validationMessages?.rule_tel as string,
    blocking: true,
  })

  const pn: ParsedPhoneNumber = $parsePhoneNumber(context._value.phonenumber, {
    regionCode: context._value.regionCode,
  })

  if (!pn.valid || !pn.typeIsMobile) {
    node.store.set(message)

    node.input({
      regionCode: context._value.regionCode,
      phonenumber: context._value.phonenumber,
      parsedPhoneNumber: {},
    })

    context?.handlers.blur(e)

    return
  } else {
    if (has(node.store, 'rule_tel')) {
      node.store.remove('rule_tel')
    }
  }

  node.input({
    regionCode: context._value.regionCode,
    phonenumber: context._value.phonenumber,
    parsedPhoneNumber: pn,
  })

  context?.handlers.blur(e)
}
</script>

<template>
  <div>
    <label
      v-if="context?.attrs['label-select']"
      id="label-select"
      for="tel-select"
      class="text-medium text-xs"
      >{{ context?.attrs['label-select'] }}*</label
    >
    <div :class="`${context.classes.tel} ${context.classes['tel-select']}`">
      <select
        id="tel-select"
        aria-describedby="label-select"
        :data-placeholder="context._value.regionCode === '' ? true : null"
        @input="(e: Event) => handleSelect(e)"
        @blur="(e: FocusEvent) => handleBlur(e)"
        :class="`${context.classes['tel-select-input']} data-[placeholder]:text-placeholder`"
        :required="node.props.validation.includes('required') ? true : false"
        :disabled="context.disabled ? true : false"
      >
        <option
          v-if="
            !context._value.regionCode && context.attrs['placeholder-select']
          "
          hidden
          disabled
          value
          data-is-placeholder="true"
          :class="`${context.classes.option} text-placeholder`"
          selected
        >
          {{ context.attrs['placeholder-select'] }}
        </option>
        <option
          v-for="option in context.options"
          :value="option"
          :class="`${context.classes.option}`"
          :selected="option === context._value.regionCode ? true : false"
        >
          {{ option }}
        </option>
      </select>
    </div>
  </div>
  <div class="w-full">
    <label
      v-if="context?.attrs['label-input']"
      id="label-input"
      for="tel-input"
      class="text-medium text-xs"
      >{{ context?.attrs['label-input'] }}*</label
    >
    <input
      type="tel"
      :id="context?.id"
      :name="context?.node.name"
      :class="`${context.classes.tel} ${context.classes['tel-input-input']}`"
      @input="(e: Event) => handleInput(e)"
      @blur="(e: FocusEvent) => handleBlur(e)"
      :value="context._value.phonenumber"
      :placeholder="context?.attrs['placeholder-input']"
      :required="node.props.validation.includes('required') ? true : false"
      :disabled="context.disabled ? true : false"
      aria-describedby="label-input"
      autocomplete="tel-national"
    />
  </div>
</template>
